import type { JourneyUser } from "./journey";

export type MetricGranularity = "day" | "month";
export type MetricChannel = "mobile" | "telegram";

/**
 * A single point on a time series — the exact shape ISSUE-350/351's eventual
 * composite `{from, to, granularity}` → envelope endpoint will emit per row.
 */
export interface MetricPoint {
  readonly period: string;
  readonly value: number;
}

/**
 * The composite-metrics envelope (ISSUE-352 rescoping note, 2026-09-03):
 * `{series: [{period, value}], total}`. One envelope corresponds to one
 * future `GET .../metrics?...&channel=` call for a single channel.
 */
export interface MetricEnvelope {
  readonly series: readonly MetricPoint[];
  readonly total: number;
}

/**
 * One channel's data for a period metric — either real series data, or an
 * explicit "no observable event" marker with a required plain-language
 * reason (ADR-052 §3). A metric's fixture carries exactly one of these per
 * channel; there is no third, silently-omitted state a renderer could
 * mistake for zero (Architect decision 2026-09-03 — the type that made every
 * channel mandatory is what let a fabricated Telegram series pass as real).
 */
export type ChannelMetric =
  | { readonly available: true; readonly envelope: MetricEnvelope }
  | { readonly available: false; readonly reason: string };

export type ChannelMetricMap = Readonly<Record<MetricChannel, ChannelMetric>>;

export interface GrowthChartFixture {
  readonly day: ChannelMetricMap;
  readonly month: ChannelMetricMap;
}

function nth(values: readonly number[], index: number): number {
  const value = values[index];
  if (value === undefined) {
    throw new Error(`toEnvelope: missing value at index ${index}`);
  }
  return value;
}

function toEnvelope(periods: readonly string[], values: readonly number[]): MetricEnvelope {
  if (periods.length !== values.length) {
    throw new Error(`toEnvelope: ${periods.length} periods but ${values.length} values`);
  }
  const series = periods.map((period, index) => ({ period, value: nth(values, index) }));
  return { series, total: values.reduce((sum, value) => sum + value, 0) };
}

function available(envelope: MetricEnvelope): ChannelMetric {
  return { available: true, envelope };
}

function unavailable(reason: string): ChannelMetric {
  return { available: false, reason };
}

const DAY_PERIODS: readonly string[] = [
  "2026-08-20",
  "2026-08-21",
  "2026-08-22",
  "2026-08-23",
  "2026-08-24",
  "2026-08-25",
  "2026-08-26",
  "2026-08-27",
  "2026-08-28",
  "2026-08-29",
  "2026-08-30",
  "2026-08-31",
  "2026-09-01",
  "2026-09-02",
];

const MONTH_PERIODS: readonly string[] = ["2026-04", "2026-05", "2026-06", "2026-07", "2026-08", "2026-09"];

/**
 * Same wording `journey.ts`'s `JourneyStage.unavailableReason` uses for the
 * identical metric — one honest-absence affordance, not two competing
 * explanations for the same fact.
 */
const TELEGRAM_NO_REGISTRATION_REASON =
  "Telegram has no Entra identity, so there is no registration event to count.";

/**
 * Dev/review fixture data for the users-registered-per-period T3 growth
 * chart (ISSUE-352 rescoping, 2026-09-03) — built against the future
 * composite-metrics envelope shape before the real endpoint (ISSUE-350's
 * eventual rebuild onto that contract) exists, same fixture-before-API
 * pattern `src/api/fixtures.ts` (ISSUE-329 Deliverable D) established.
 *
 * **Mobile only.** Telegram has no registration event anywhere (ADR-052 §3 —
 * `registeredAt` is always `null`, and Telegram's only sanctioned proxy,
 * `lastActivityAt`, is an *activity* proxy sourced from document uploads,
 * not a registration proxy). Corrected 2026-09-03 (Architect decision): a
 * prior version plotted a fabricated Telegram numeric series here; if a
 * registration proxy is ever wanted, defining one is ISSUE-350's decision to
 * make explicitly, never a UI fixture's to assume.
 */
export const usersRegisteredFixture: GrowthChartFixture = {
  day: {
    mobile: available(toEnvelope(DAY_PERIODS, [3, 4, 2, 5, 6, 4, 7, 5, 8, 6, 9, 7, 10, 8])),
    telegram: unavailable(TELEGRAM_NO_REGISTRATION_REASON),
  },
  month: {
    mobile: available(toEnvelope(MONTH_PERIODS, [64, 78, 91, 103, 118, 132])),
    telegram: unavailable(TELEGRAM_NO_REGISTRATION_REASON),
  },
};

/**
 * Documents-indexed-per-period fixture — same envelope shape and
 * fixture-before-API convention as {@link usersRegisteredFixture}. Unlike
 * registration, document upload is a real event on both channels (ADR-052
 * §3's `lastActivityAt` upload-proxy for Telegram is itself sourced from
 * document activity), so both channels are `available` here.
 */
export const documentsIndexedFixture: GrowthChartFixture = {
  day: {
    mobile: available(toEnvelope(DAY_PERIODS, [9, 11, 7, 14, 16, 10, 18, 13, 20, 15, 22, 17, 24, 19])),
    telegram: available(toEnvelope(DAY_PERIODS, [4, 2, 5, 3, 1, 6, 4, 5, 2, 3, 5, 4, 6, 5])),
  },
  month: {
    mobile: available(toEnvelope(MONTH_PERIODS, [172, 198, 231, 264, 289, 312])),
    telegram: available(toEnvelope(MONTH_PERIODS, [52, 61, 58, 69, 74, 83])),
  },
};

/**
 * Sample per-user rows for the Journey Funnel (ISSUE-352 AC1) — dev/review
 * fixture data only, relocated here from `src/api/fixtures.ts` (ISSUE-455
 * Deliverable 3) once `GET /users` became a bare count and could no longer
 * supply a per-user shape for this dashboard-only sample. Values unchanged
 * from the prior `usersFixture.users` set the funnel consumed before that
 * change. The Journey Funnel has never been wired to a live endpoint (its
 * own doc comment); a per-user roster to drive it live is not a shape
 * `GET /users` can provide again.
 */
export const journeyUsersFixture: readonly JourneyUser[] = [
  {
    userRef: "entra#3fa85f64-5717-4562-b3fc-2c963f66afa6",
    channel: "mobile",
    registeredAt: "2026-08-01T10:12:03Z",
  },
  {
    userRef: "123456789",
    channel: "telegram",
    registeredAt: null,
  },
  {
    userRef: "entra#7c9e6679-7425-40de-944b-e07fc1f90ae7",
    channel: "mobile",
    registeredAt: "2026-06-14T08:30:00Z",
  },
  {
    userRef: "987654321",
    channel: "telegram",
    registeredAt: null,
  },
];
